import { Box, styled } from '@material-ui/core';
import {
  cleanChatSearchStateAgentConsole,
  oversetActiveChat,
} from '@src/actions/agentconsole';
import { DARK_BG_CARD } from '@src/muithemes/darkModeColors';
import useChatActions from '@src/hooks/useChatActions';
import { AgentChatTransferStatus } from '@src/model/frontendmodel';
import {
  activeChatIdSelector,
  activeChatSideSelector,
  chatActivationActionSelector,
  chatPickupActionSelector,
} from '@src/selectors/chats';
import { getUserId } from '@src/selectors/user';
import { RootState } from '@src/store/reduxstore';
import {
  ChatActionsStatus,
  ChatSideType,
  ChatTray,
  ChatTrayXfer,
} from '@src/types/chats';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ChatNewContactIndicator from '@src/components/ChatListItem/ChatNewContactIndicator';
import Typography from '../UI/Typography';
import ChatAssignedBy from './ChatAssignedBy';
import ChatContactAvatar from './ChatContactAvatar';
import ChatSmartFormatDate from './ChatMessageTime';
import ChatPinnedIndicator from './ChatPinnedIndicator';
import ChatStatusIndicator from './ChatStatusIndicator';
import ChatTransferIndicator from './ChatTransferIndicator';
import LastMessagePreview from './LastMessagePreview';

type ChatListItemType = {
  chat: ChatTray;
  showDivider?: boolean;
};

const ChatListItem: React.FC<ChatListItemType> = ({
  chat,
  showDivider = true,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [isHovered, setIsHovered] = useState(false);

  const agentId = useSelector(getUserId);
  const activeChatId = useSelector(activeChatIdSelector);
  const activeChatSide = useSelector(activeChatSideSelector);

  //prettier-ignore
  const isActiveChat = useMemo(() => activeChatId && activeChatId === chat.id, [activeChatId, chat.id]);
  const isChatRequest = activeChatSide === ChatSideType.REQUEST;
  const isBotSide = activeChatSide === ChatSideType.BOT;

  const isAwaitingChatActivation = useSelector((state: RootState) => {
    const chatPickupAction = chatPickupActionSelector(state);
    const chatActivationAction = chatActivationActionSelector(state);
    return (
      chatPickupAction?.status === ChatActionsStatus.AWAIT ||
      chatActivationAction?.status === ChatActionsStatus.AWAIT
    );
  });

  const { pickupChat, pickupChatTransfer, activateChat } = useChatActions();

  const contactName = useMemo(
    () =>
      chat?.contact?.fullName ??
      formatMessage({ id: 'Chat.unknownContactName' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat.contact]
  );

  const assignedAgent = useMemo(
    () => chat?.assignedBy?.agent,
    [chat?.assignedBy?.agent]
  );

  const onChatSelect = () => {
    const transfer = chat?.chatXferRequest ?? ({} as ChatTrayXfer);
    // Don't do anything with an accepted transferred chat
    // Wait if we are waiting for the result of a previous set active chat request
    if (
      transfer?.status === AgentChatTransferStatus.ACCEPTED ||
      isAwaitingChatActivation
    )
      return;

    // Check if this is actually an AgentChat Xfer requested by another agent. In that case, we are picking it up
    if (
      transfer &&
      transfer?.sourceAgent?.id !== agentId &&
      transfer?.status === AgentChatTransferStatus.REQUESTED
    ) {
      transfer.status = AgentChatTransferStatus.PICKED_UP;
      pickupChatTransfer(transfer);
    } else if (isActiveChat) {
      // Is this the currently active chat? If so, no need to set it as active once more!
      dispatch(oversetActiveChat(chat.id));
    } else if (
      !isBotSide &&
      (isChatRequest || (!chat.assignedTo && !transfer))
    ) {
      pickupChat(chat.id, agentId);
    } else {
      activateChat(chat.id, agentId);
    }
    dispatch(cleanChatSearchStateAgentConsole(chat.id));
  };

  return (
    <ChatItemStyled
      //prettier-ignore
      className={`
        ${isActiveChat ? 'active' : ''}
        ${showDivider ? 'divider' : ''}`
      }
      data-testid="chat-item"
      data-chat-id={chat.id}
      data-active={isActiveChat}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onChatSelect}
    >
      <div className="chat-item-content">
        <ChatContactAvatar
          src={chat.contact.avatarUrl}
          name={chat.contact.fullName}
          badgeProvider={chat?.provider}
        />

        <Box
          ml={1}
          width="inherit"
          display="flex"
          flexDirection="column"
          gridGap={3}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontWeight="medium"
              title={contactName}
              className="contact-name"
            >
              {contactName}
            </Typography>
            <Box display="flex" alignItems="center" gridGap={5}>
              <ChatTransferIndicator chat={chat} />
              <ChatStatusIndicator chat={chat} />
              <ChatPinnedIndicator chat={chat} isHovered={isHovered} />
              <ChatNewContactIndicator chat={chat} />

              {Boolean(chat?.messagesSinceLastResponse) && (
                <Box className="count-messages">
                  {chat?.messagesSinceLastResponse}
                </Box>
              )}
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography className="last-message">
              <LastMessagePreview chat={chat} />
            </Typography>

            <ChatSmartFormatDate chat={chat} />
          </Box>

          {!!chat?.businessProcessNode?.id && (
            <Typography
              title={chat?.businessProcessNode?.code}
              className="business-message"
            >
              {chat?.businessProcessNode?.code ?? ''}
            </Typography>
          )}

          {!!chat?.viaBotAccountAlias && (
            <Typography
              className="from-message"
              title={chat?.viaBotAccountAlias}
            >
              {formatMessage({ id: 'Chat.viaBotAccountLbl' })}
              {chat.viaBotAccountAlias}
            </Typography>
          )}
        </Box>
      </div>
      {assignedAgent?.fullName && (
        <ChatAssignedBy agentAssigned={assignedAgent?.fullName} />
      )}
    </ChatItemStyled>
  );
};

const ChatItemStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'inherit',
  width: '100%',
  padding: '0 15px',
  backgroundColor:
    theme.palette.type === 'dark'
      ? DARK_BG_CARD
      : theme.palette.background.paper,
  zIndex: 1,
  '&:not(.active):hover': { backgroundColor: theme.chatItem.hover },
  '&.active': { backgroundColor: theme.chatItem.activeBgColor },
  ...(theme.palette.type === 'dark' && {
    '& a': { color: '#9AA0AA !important', textDecoration: 'underline' },
  }),

  '& .chat-item-content': {
    flex: '1 1 100%',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },

  '&.divider:not(.active):after': {
    height: 1,
    content: "''",
    position: 'absolute',
    width: '86%',
    bottom: 0,
    left: '7%',
    backgroundColor: theme.palette.divider,
  },

  '& .contact-name': {
    fontSize: '1rem',
    lineHeight: '1.1rem',
    //text-overflow
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 120,
  },

  '& .count-messages': {
    width: 22,
    height: 16,
    color: '#FFF',
    display: 'inline-flex',
    backgroundColor: '#F27468',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 11,
    fontWeight: 600,
    fontSize: 11,
  },

  '& .last-message': {
    color: theme.palette.text.secondary,
    fontSize: '.8rem',
    lineHeight: '1.2',
    maxWidth: 120,
    display: '-webkit-box',
    //for-markdown
    lineClamp: 1,
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
  },

  '& .last-message, & .last-message *': {
    fontWeight: '400 !important',
  },

  '& .business-message, & .from-message, & .last-message': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '& .business-message, & .from-message': {
    position: 'absolute',
    bottom: 2,
    maxWidth: '40%',
    fontSize: '.66rem',
    color: theme.palette.text.secondary,
  },

  '& .business-message': {
    left: 0,
  },

  '& .from-message': {
    right: 0,
  },
}));

export default ChatListItem;
